import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api, { axiosClient, userAPI } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { pushToDataLayer } from '../utils/dataLayer';

/**
 * Component that manages Auth0 token synchronization with localStorage
 * Ensures the access token is available for API requests
 * Also handles impersonation mode where an admin views as a customer
 */
const AuthTokenManager: React.FC = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();
  const { setUser, setIsLoadingUser } = useUser();
  const { isImpersonating, impersonatedUser } = useImpersonation();

  // Handle impersonation mode - set user context from impersonated user
  useEffect(() => {
    if (isImpersonating && impersonatedUser) {
      setIsLoadingUser(true);

      // Fetch fresh user data for the impersonated user using the impersonation token
      const fetchImpersonatedUserData = async () => {
        try {
          const response = await userAPI.getMe();
          const userData = response.data;

          let hasActiveSessionBundle = false;
          try {
            const sessionBundles = await api.getUserSessionBundles();
            hasActiveSessionBundle = sessionBundles.some((sessionBundle: any) => sessionBundle.sessionsRemaining > 0);
          } catch (sessionBundleError) {
            console.warn('Failed to fetch sessionBundles for impersonated user:', sessionBundleError);
          }

          setUser({ ...userData, hasActiveSessionBundle });
        } catch (error) {
          console.error('Failed to fetch impersonated user data:', error);
          // Use the basic impersonated user data from the context
          setUser({
            id: impersonatedUser.id,
            email: impersonatedUser.email,
            name: impersonatedUser.name,
            hasActiveSessionBundle: false
          });
        } finally {
          setIsLoadingUser(false);
        }
      };

      fetchImpersonatedUserData();
    }
  }, [isImpersonating, impersonatedUser, setUser, setIsLoadingUser]);

  useEffect(() => {
    // Skip normal auth flow when impersonating
    if (isImpersonating) {
      return;
    }

    const setAuthToken = async () => {
      if (isLoading) {
        return;
      }

      if (isAuthenticated) {
        try {
          setIsLoadingUser(true);
          const token = await getAccessTokenSilently();
          
          localStorage.setItem('auth_token', token);
          axiosClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // After setting token, fetch user data and sessionBundles
          let userData = null;
          let hasActiveSessionBundle = false;
          
          // Try to get user data first
          try {
            const response = await userAPI.getMe();
            userData = response.data;
            hasActiveSessionBundle = userData.hasActiveSessionBundle || false;
          } catch (userError) {
            console.warn('Failed to fetch user data:', userError);
          }
          
          // Always check sessionBundles to get accurate active session bundle status
          try {
            const sessionBundles = await api.getUserSessionBundles();
            // Check if user has any sessionBundles with sessions remaining
            hasActiveSessionBundle = sessionBundles.some((sessionBundle: any) => sessionBundle.sessionsRemaining > 0);
          } catch (sessionBundleError) {
            console.warn('Failed to fetch sessionBundles:', sessionBundleError);
          }
          
          // Set user with updated session bundle status
          if (userData) {
            setUser({ ...userData, hasActiveSessionBundle });
          } else {
            // Create a minimal user object if we couldn't get user data
            setUser({
              id: 'unknown',
              email: '',
              name: '',
              hasActiveSessionBundle
            });
          }
          
          // Track login/signup event (only once per session, using sessionStorage to prevent duplicates on reload)
          const sessionKey = user?.sub ? `auth_event_fired_${user.sub}` : null;
          const hasTrackedInSession = sessionKey ? sessionStorage.getItem(sessionKey) : false;

          if (!hasTrackedInSession && user && userData && sessionKey) {
            // Mark as tracked immediately to prevent duplicates
            sessionStorage.setItem(sessionKey, 'true');

            const isNewUser = (() => {
              if (!userData.created_at || !userData.server_time) return false;
              const createdAt = new Date(userData.created_at).getTime();
              const serverTime = new Date(userData.server_time).getTime();
              const thirtySecondsMs = 30 * 1000;
              return (serverTime - createdAt) < thirtySecondsMs;
            })();

            let phone = user.phone_number || '';
            try {
              const profileResponse = await userAPI.getProfile();
              if (profileResponse.data?.phoneNumber) {
                phone = profileResponse.data.phoneNumber;
              }
            } catch {
              // Profile may not exist for new users, use Auth0 phone or empty
            }

            // Determine authentication method (Google OAuth vs email/password)
            const authMethod = user.sub?.startsWith('google-oauth2|') ? 'Google' : 'Auth0';

            pushToDataLayer(isNewUser ? 'sign_up' : 'login', {
              method: authMethod,
              customer: {
                email: user.email || '',
                phone: phone
              }
            });
          }
        } catch (err) {
          console.error('Error setting auth token or fetching user:', err);
          setUser(null);
        } finally {
          setIsLoadingUser(false);
        }
      } else {
        // Not authenticated, clear token and user
        localStorage.removeItem('auth_token');
        delete axiosClient.defaults.headers.common['Authorization'];
        setUser(null);
        setIsLoadingUser(false);
      }
    };

    setAuthToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, setUser, setIsLoadingUser, user, isImpersonating]);

  return null;
};

export default AuthTokenManager;